import { NegotiationStatus, DealStatus, CommittingServicingEvent, NegotiationDetail, AuditDetail, DealEvent } from 'api'
import { getTimeLeft } from 'utils'
import { timeConstants } from 'view/components/timers'
import { isAuditDetail, isLastLook } from './utils'

export enum ToastColor {
  RED,
  YELLOW,
  GREEN,
  BLUE,
  PURPLE,
  GRAY
}

export enum ToastPriority {
  LOW,
  MEDIUM,
  HIGH,
  URGENT
}

export interface ToastNotificationMessage {
  tradingEntityName: string
  organisationName: string
  statusText: string
  subStatusText: string
  subStatusTextGrey: boolean
  statusColour: ToastColor
  priority: ToastPriority
  count: number
  isDead: boolean
  isImportant: boolean
  negotiationProps?: {
    status: NegotiationStatus
  }
  dealProps?: {
    status: DealStatus
    servicingEvent?: CommittingServicingEvent
  }
  onClickPath: string
}

export const getToastPriorityFromToastColor = (color: ToastColor): ToastPriority => {
  switch (color) {
    case ToastColor.BLUE:
      return ToastPriority.MEDIUM
    case ToastColor.GREEN:
      return ToastPriority.MEDIUM
    case ToastColor.PURPLE:
      return ToastPriority.MEDIUM
    case ToastColor.YELLOW:
      return ToastPriority.HIGH
    case ToastColor.RED:
      return ToastPriority.HIGH
    case ToastColor.GRAY:
    default:
      return ToastPriority.LOW
  }
}

export function getServicingStatusToastColor(servicingEvent: CommittingServicingEvent): ToastColor {
  switch (servicingEvent) {
    case DealEvent.SERVICING_START_RERATE:
    case DealEvent.SERVICING_START_SUBSTITUTE:
    case DealEvent.SERVICING_START_TERMINATE:
    case DealEvent.SERVICING_START_RETURN_RECALL:
    case DealEvent.SERVICING_START_ROLL_IN_PLACE:
    case DealEvent.SERVICING_START_TRADER_AMEND:
    case DealEvent.SERVICING_SEND:
      return ToastColor.BLUE

    case DealEvent.SERVICING_ACCEPT:
      return ToastColor.GREEN

    case DealEvent.SERVICING_REJECT:
    case DealEvent.SERVICING_WITHDRAW:
      return ToastColor.RED

    default:
      return ToastColor.GRAY
  }
}

export function getLiveStatusToastColor(negotiationAuditDetail: NegotiationDetail | AuditDetail, withExpiryWarning?: boolean): ToastColor {
  if (
    !isAuditDetail(negotiationAuditDetail) &&
    withExpiryWarning &&
    negotiationAuditDetail.negotiation.status === NegotiationStatus.QUOTED &&
    !isLastLook(negotiationAuditDetail.negotiation)
  ) {
    const otwEndTime = negotiationAuditDetail.negotiation.otwEndTime || negotiationAuditDetail.negotiation.rfqEndTime
    const timeLeft = getTimeLeft(otwEndTime)

    if (timeLeft < timeConstants.defaultUrgent) {
      return ToastColor.RED
    } else if (timeLeft < timeConstants.defaultExpiring) {
      return ToastColor.YELLOW
    }
  }

  switch (negotiationAuditDetail.negotiation.status) {
    case NegotiationStatus.REQUESTED_WITHOUT_PRICE:
      return ToastColor.BLUE

    case NegotiationStatus.WITHDRAWN:
      return ToastColor.PURPLE

    case NegotiationStatus.REQUESTED_WITH_PRICE:
      return ToastColor.BLUE

    case NegotiationStatus.QUOTED:
      return ToastColor.BLUE

    case NegotiationStatus.QUOTED_SUBJECT:
      return ToastColor.BLUE

    case NegotiationStatus.CANCELLED:
    case NegotiationStatus.REJECTED:
    case NegotiationStatus.MISSED:
    case NegotiationStatus.EXPIRED:
      return ToastColor.RED

    case NegotiationStatus.ACCEPTED:
      return ToastColor.GREEN

    case NegotiationStatus.ITEMS_IN_DIFFERENT_STATES:
    case NegotiationStatus.ITEMS_IN_DIFFERENT_STATES_REJECTED_EXPIRED:
    case NegotiationStatus.ITEMS_IN_DIFFERENT_STATES_TERMINATED:
      return ToastColor.GRAY

    default:
      return ToastColor.BLUE
  }
}
